import { Download } from 'lucide-react';
import { useEffect, useId, useState } from 'react';
import { toast } from 'react-hot-toast';
import { Button } from '@/components/ui/NewButton';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import type { ChronologyEventLight } from '@/hooks/useChronology';
import { cn } from '@/lib/utils';
import type { RegionalDateLocale } from '@/utils/date-utils';
import { downloadChronologyExcel, downloadChronologyWord } from './export';
import { TOOLBAR_BUTTON_ACTIVE_CLASSNAME, TOOLBAR_BUTTON_CLASSNAME, TOOLBAR_LABEL_CLASSNAME } from './toolbar-styles';

function exportErrorMessage(error: unknown, fallback: string): string {
  const detail = (error as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  return typeof detail === 'string' ? detail : fallback;
}

interface ExportPopoverProps {
  matterGuid?: string;
  chronologyId?: string | null;
  filteredEvents: ChronologyEventLight[];
  locale: RegionalDateLocale;
}

export function ExportPopover({
  matterGuid,
  chronologyId = null,
  filteredEvents,
  locale,
}: ExportPopoverProps) {
  const detailId = useId();
  const sourcesId = useId();
  const sourceDocumentsTableId = useId();
  const crimsonLinksId = useId();
  const [open, setOpen] = useState(false);
  const [dateFormat, setDateFormat] = useState(locale === 'us' ? 'MMM d, yyyy' : 'd MMM yyyy');
  const [includeSources, setIncludeSources] = useState(true);
  const [includeDetail, setIncludeDetail] = useState(true);
  const [includeSourceDocumentsTable, setIncludeSourceDocumentsTable] = useState(false);
  const [includeCrimsonDocumentLinks, setIncludeCrimsonDocumentLinks] = useState(false);
  const [exporting, setExporting] = useState<'word' | 'excel' | null>(null);

  useEffect(() => {
    setDateFormat(locale === 'us' ? 'MMM d, yyyy' : 'd MMM yyyy');
  }, [locale]);

  const commonOptions = {
    matterGuid: matterGuid ?? '',
    chronologyId,
    filteredEvents,
    dateFormat,
    includeSources,
    includeDetail,
    includeSourceDocumentsTable,
    includeCrimsonDocumentLinks,
    onComplete: () => setOpen(false),
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          title="Export the events shown to Word or Excel"
          aria-label="Export chronology"
          className={cn(TOOLBAR_BUTTON_CLASSNAME, open && TOOLBAR_BUTTON_ACTIVE_CLASSNAME)}
        >
          <Download className="h-3.5 w-3.5 shrink-0 text-gray-400" />
          <span className={TOOLBAR_LABEL_CLASSNAME}>Export</span>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[260px] p-0" align="end" side="bottom" sideOffset={6}>
        <div className="px-3 pt-3 pb-2 border-b border-gray-100 flex items-center justify-between">
          <p className="text-xs font-semibold text-gray-700">Export options</p>
          <p className="text-[11px] text-gray-500">{filteredEvents.length} {filteredEvents.length === 1 ? 'event' : 'events'}</p>
        </div>
        <div className="p-3 space-y-3">
          <div className="space-y-1.5">
            <Label className="text-[11px] font-medium text-gray-500">Date format</Label>
            <Select value={dateFormat} onValueChange={setDateFormat}>
              <SelectTrigger
                tabIndex={-1}
                className="h-8 text-xs focus:ring-0 focus:ring-offset-0"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={locale === 'us' ? 'MMM d, yyyy' : 'd MMM yyyy'}>
                  {locale === 'us' ? 'Apr 25, 2026' : '25 Apr 2026'}
                </SelectItem>
                <SelectItem value={locale === 'us' ? 'MM/dd/yyyy' : 'dd/MM/yyyy'}>
                  {locale === 'us' ? '04/25/2026' : '25/04/2026'}
                </SelectItem>
                <SelectItem value="yyyy-MM-dd">2026-04-25</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2">
            <Checkbox
              id={detailId}
              tabIndex={-1}
              checked={includeDetail}
              onCheckedChange={value => setIncludeDetail(value === true)}
            />
            <Label htmlFor={detailId} className="text-xs text-gray-600 cursor-pointer">Include detail column</Label>
          </div>
          <div className="flex items-center gap-2">
            <Checkbox
              id={sourcesId}
              tabIndex={-1}
              checked={includeSources}
              onCheckedChange={(value) => {
                const checked = value === true;
                setIncludeSources(checked);
                if (!checked) setIncludeCrimsonDocumentLinks(false);
              }}
            />
            <Label htmlFor={sourcesId} className="text-xs text-gray-600 cursor-pointer">Include sources column</Label>
          </div>
          {includeSources && (
            <div className="ml-6 flex items-center gap-2">
              <Checkbox
                id={crimsonLinksId}
                tabIndex={-1}
                checked={includeCrimsonDocumentLinks}
                onCheckedChange={value => setIncludeCrimsonDocumentLinks(value === true)}
              />
              <Label htmlFor={crimsonLinksId} className="text-xs text-gray-600 cursor-pointer">Include Crimson document links</Label>
            </div>
          )}
          <div className="flex items-center gap-2">
            <Checkbox
              id={sourceDocumentsTableId}
              tabIndex={-1}
              checked={includeSourceDocumentsTable}
              onCheckedChange={value => setIncludeSourceDocumentsTable(value === true)}
            />
            <Label htmlFor={sourceDocumentsTableId} className="text-xs text-gray-600 cursor-pointer">Include source documents table</Label>
          </div>
        </div>
        <div className="px-3 pb-3 flex gap-2">
          <Button
            onClick={async () => {
              if (!matterGuid) return;
              setExporting('word');
              try {
                await downloadChronologyWord(commonOptions);
              } catch (error) {
                toast.error(exportErrorMessage(error, 'Failed to export chronology as Word'));
              } finally {
                setExporting(null);
              }
            }}
            disabled={exporting !== null || !matterGuid}
            loading={exporting === 'word'}
            loadingText="Preparing"
            text="Word"
            icon={Download}
            size="sm"
            variant="outline"
            className="flex-1"
          />
          <Button
            onClick={async () => {
              if (!matterGuid) return;
              setExporting('excel');
              try {
                await downloadChronologyExcel(commonOptions);
              } catch (error) {
                toast.error(exportErrorMessage(error, 'Failed to export chronology as Excel'));
              } finally {
                setExporting(null);
              }
            }}
            disabled={exporting !== null || !matterGuid}
            loading={exporting === 'excel'}
            loadingText="Preparing"
            text="Excel"
            icon={Download}
            size="sm"
            variant="outline"
            className="flex-1"
          />
        </div>
      </PopoverContent>
    </Popover>
  );
}
